/** @jsxImportSource react */
import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Link as RouterLink } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { formatCompact, formatNumber } from '../../utils/numbers'

const BAR_SIZE = 20 // ≤ 24px marks, the rest of the band stays air
const ROW_HEIGHT = 34

/** Tooltip: value first (strong), label second, keyed with a short line in the series color. */
const ChartTooltip = ({ active, payload, label, measureLabel, measureType, labelFormatter }) => {
  const theme = useTheme()
  if (!active || !payload?.length) return null
  const value = payload[0].value
  return (
    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.75, boxShadow: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 12, height: 2, bgcolor: theme.palette.chart.series1, borderRadius: 1 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatNumber(value, measureType)}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {measureLabel} · {labelFormatter ? labelFormatter(label) : label}
      </Typography>
    </Box>
  )
}

/** Headline figure with an optional change vs the previous period (arrow + words, never color alone). */
export const NumberView = ({ data, rangeLabel, compare }) => {
  const theme = useTheme()
  const value = data.value
  const prev = data.previousValue
  let delta = null
  if (compare && prev !== null && prev !== undefined && value !== null) {
    const diff = Number(value) - Number(prev)
    const pct = Number(prev) === 0 ? null : (diff / Math.abs(Number(prev))) * 100
    delta = { diff, pct }
  }
  return (
    <Box>
      <Typography sx={{ fontSize: 36, fontWeight: 600, lineHeight: 1.15 }}>{formatCompact(value, data.measureType)}</Typography>
      {delta && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: delta.diff > 0 ? theme.palette.chart.good : delta.diff < 0 ? theme.palette.chart.bad : 'text.secondary' }}>
          {delta.diff > 0 ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : delta.diff < 0 ? <ArrowDownwardIcon sx={{ fontSize: 16 }} /> : null}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {delta.diff === 0 ? 'No change' : `${delta.diff > 0 ? 'Up' : 'Down'} ${delta.pct === null ? formatCompact(Math.abs(delta.diff), data.measureType) : `${Math.abs(delta.pct).toFixed(0)}%`}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">vs previous period</Typography>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{data.measureLabel} · {rangeLabel}</Typography>
    </Box>
  )
}

/** Horizontal bars, one color (identity is the label, not the hue); value at each bar's tip. */
export const BarView = ({ data, onSelect }) => {
  const theme = useTheme()
  const rows = (data.series || []).map((b) => ({ ...b, value: b.value === null ? 0 : Number(b.value) }))
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No data</Typography>
  const labelWidth = Math.min(160, Math.max(60, ...rows.map((r) => r.label.length * 7)))
  return (
    <Box sx={{ height: rows.length * ROW_HEIGHT + 16 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 0 }} barCategoryGap={2}>
          <CartesianGrid horizontal={false} stroke={theme.palette.chart.grid} />
          <XAxis type="number" hide domain={[0, 'dataMax']} />
          <YAxis type="category" dataKey="label" width={labelWidth} tickLine={false} axisLine={{ stroke: theme.palette.chart.axis }}
            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
          <Tooltip cursor={{ fill: theme.palette.action.hover }} content={<ChartTooltip measureLabel={data.measureLabel} measureType={data.measureType} />} />
          <Bar dataKey="value" fill={theme.palette.chart.series1} barSize={BAR_SIZE} radius={[0, 4, 4, 0]} isAnimationActive={false}
            cursor={onSelect ? 'pointer' : undefined} onClick={onSelect ? (row) => onSelect(row.payload ?? row) : undefined}>
            <LabelList dataKey="value" position="right" formatter={(v) => formatCompact(v, data.measureType)} style={{ fill: theme.palette.text.primary, fontSize: 12 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}

const bucketLabel = (interval) => (iso) => {
  try {
    const d = parseISO(iso)
    return format(d, interval === 'MONTH' || interval === 'QUARTER' ? 'MMM yyyy' : interval === 'YEAR' ? 'yyyy' : 'd MMM')
  } catch {
    return iso
  }
}

/** Single-series trend: 2px line over a 10% wash; the crosshair snaps to the nearest bucket. */
export const TrendView = ({ data, interval }) => {
  const theme = useTheme()
  const points = (data.points || []).map((p) => ({ bucket: p.bucket, value: p.value === null ? null : Number(p.value) }))
  if (points.length === 0) return <Typography variant="body2" color="text.secondary">No data</Typography>
  const label = bucketLabel(interval)
  const last = points[points.length - 1]
  return (
    <Box sx={{ height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={theme.palette.chart.grid} />
          <XAxis dataKey="bucket" tickFormatter={label} tickLine={false} axisLine={{ stroke: theme.palette.chart.axis }}
            tick={{ fill: theme.palette.chart.axisText, fontSize: 11 }} minTickGap={24} />
          <YAxis width={48} tickLine={false} axisLine={false} allowDecimals={false}
            tick={{ fill: theme.palette.chart.axisText, fontSize: 11 }} tickFormatter={(v) => formatCompact(v)} />
          <Tooltip cursor={{ stroke: theme.palette.chart.axis, strokeWidth: 1 }}
            content={<ChartTooltip measureLabel={data.measureLabel} measureType={data.measureType} labelFormatter={label} />} />
          <Area type="linear" dataKey="value" stroke={theme.palette.chart.series1} strokeWidth={2} fill={theme.palette.chart.series1}
            fillOpacity={0.1} isAnimationActive={false} dot={false}
            activeDot={{ r: 4, fill: theme.palette.chart.series1, stroke: theme.palette.background.paper, strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
      <Typography variant="caption" color="text.secondary">
        Latest: <strong>{formatNumber(last.value, data.measureType)}</strong> ({label(last.bucket)})
      </Typography>
    </Box>
  )
}

export const TopListView = ({ data }) => {
  if (!data.rows?.length) return <Typography variant="body2" color="text.secondary">No records</Typography>
  return (
    <Table size="small">
      <TableBody>
        {data.rows.map((row, i) => (
          <TableRow key={row.id}>
            <TableCell sx={{ width: 28, color: 'text.secondary', pl: 0 }}>{i + 1}</TableCell>
            <TableCell>
              <Link component={RouterLink} to={`/records/${row.entityType}/${row.id}`} underline="hover">{row.title}</Link>
            </TableCell>
            {data.measureType && (
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', pr: 0 }}>{formatNumber(row.value, data.measureType)}</TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/** The table twin of a chart: every value reachable without hovering. */
export const DataTableView = ({ data, kind, interval }) => {
  const rows = kind === 'BAR'
    ? (data.series || []).map((b) => [b.label, b.value])
    : (data.points || []).map((p) => [bucketLabel(interval)(p.bucket), p.value])
  return (
    <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: 0 }}>{kind === 'BAR' ? 'Group' : 'Period'}</TableCell>
            <TableCell align="right" sx={{ pr: 0 }}>{data.measureLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([label, value], i) => (
            <TableRow key={i}>
              <TableCell sx={{ pl: 0 }}>{label}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', pr: 0 }}>{formatNumber(value, data.measureType)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
